import extractStyles from './extractStyles.js';

/**
 * Get style object for HPP library
 * @param {'creditCard'|'directDebit'} paymentType
 * @param {boolean} isRtl determine if it's RTL locale
 * @returns {object}
 */
export default (paymentType, isRtl = false) => {
	// DOM Helper elements for styling
	const inputBase = document.querySelector('.hppInput_base');
	const labelBase = document.querySelector('.hppLabel_base');
	const inputError = document.querySelector('.hppInput_error');
	const labelError = document.querySelector('.hppLabel_error');
	const inputErrorFocus = document.querySelector('.hppInput_errorFocus');
	const labelErrorFocus = document.querySelector('.hppLabel_errorFocus');
	const inputActive = document.querySelector('.hppInput_active');
	const labelActive = document.querySelector('.hppLabel_active');
	const inputFocus = document.querySelector('.hppInput_focus');
	const labelFocus = document.querySelector('.hppLabel_focus');
	const inputAutofill = document.querySelector('.hppInput_autofill');
	const cartBody = document.querySelector('body');
	// Styles (extracted from DOM elements + general)
	const styles = {
		'@hpp_div': {
			width: '100%'
		},
		'#hpp_avast': {
			...extractStyles(inputBase, ['fontFamily']),
			padding: '2px 0 1px',
			...extractStyles(cartBody, ['zoom'])
		},
		'#hpp_avast input': extractStyles(inputBase, ['position', 'top', 'color', 'fontFamily', 'height', 'width', 'padding-top', 'background']),
		'#hpp_avast input:not(.error) + label': extractStyles(labelBase, ['color', 'fontFamily']),
		'#hpp_avast input:focus + label': extractStyles(labelFocus, ['top', 'left', 'color']),
		'#hpp_avast input.active + label': extractStyles(labelActive, ['top', 'left']),
		'#hpp_avast input:focus': extractStyles(inputFocus, ['color', 'height', 'position', 'top']),
		'#hpp_avast input:-webkit-autofill, #hpp_avast input:-webkit-autofill:hover, #hpp_avast input:-webkit-autofill:focus, #hpp_avast input:-webkit-autofill:active': extractStyles(inputAutofill, ['-webkit-text-fill-color'], false, true, true),
		'#hpp_avast input.active': extractStyles(inputActive, ['color', 'height', 'position', 'top']),
		'#hpp_avast .error label': extractStyles(labelError, ['color']),
		'#hpp_avast .error input:not(:focus)': extractStyles(inputError, ['color']),
		'#hpp_avast input.error:focus': extractStyles(inputErrorFocus, ['color']),
		'#hpp_avast input.error:focus + label': extractStyles(labelErrorFocus, ['color'])
	};
	// Shared styling for HPP elements
	styles['#hpp_avast [id$="Container"]'] = {
		border: 0,
		height: '100%'
	};
	styles['#hpp_avast .error'] = {
		'font-weight': 'normal'
	};
	styles['#hpp_avast label'] = {
		'text-transform': 'capitalize',
		cursor: 'text',
		transition: 'all .1s ease',
		'z-index': 2
	};
	styles['.hppInputWrapper'] = {
		display: 'flex',
		'align-items': 'center',
		width: '100%',
		height: '100%',
		left: 'auto'
	};
	// Styling for creditCard payment method elements
	if (paymentType === 'creditCard' || paymentType === 'bancontact') {
		styles['#hpp_avast.cvcOnly'] = {
			margin: 0,
			padding: '0.8rem 0 0.8rem 1.6rem'
		};
		styles['#hpp_avast.cvcOnly .ccCVCContainer input:not(:focus) + label'] = {
			opacity: 0
		};
		styles['#hpp_avast.cvcOnly .ccCVCContainer input.visited + label'] = {
			opacity: 1
		};
		styles['#hpp_avast .ccNumberWrapper input:not(:focus) + label'] = {
			opacity: 0
		};
		styles['#hpp_avast .ccNumberWrapper input.visited + label'] = {
			opacity: 1
		};
		styles['#hpp_avast #ccContainer'] = {
			'padding-left': '1.6rem'
		};
		styles['#hpp_avast .card_logos'] = {
			'align-items': 'center',
			bottom: 0,
			top: 0
		};
		styles['.ccNumberWrapper'] = {
			display: 'flex',
			'flex-basis': '59%',
			'max-width': '59%'
		};
		styles['.ccExpCVCWrapper'] = {
			display: 'flex',
			'flex-basis': '45%',
			'max-width': '45%'
		};
	}
	// Styling for bancontact payment method only
	if (paymentType === 'bancontact') {
		// Media query styles "hack". Keep it mind that we work with the iframe width (Bancotact CC field), not the page width
		const bancontactMediaStyles = `
			@media only screen and (max-width: 480px) {
				.ccNumberWrapper {
					flex-basis: 70%;
					max-width: 70%;
				}
				.ccExpCVCWrapper {
					flex-basis: 30%;
					max-width: 30%;
				}
			}`;
		styles[bancontactMediaStyles] = {};
	}
	// Styling for directDebit payment method elements
	if (paymentType === 'directDebit') {
		styles['#hpp_avast'] = {
			...styles['#hpp_avast'],
			width: '100%',
			padding: '0'
		};
		styles['#hpp_avast #ddContainer'] = {
			border: '0',
			width: '100%'
		};
		styles['#hpp_avast input'] = {
			...styles['#hpp_avast input'],
			border: '0',
			'border-top': '1px solid transparent',
			width: '100%',
			height: '100%',
			padding: '2.1rem 1.6rem 0.7rem'
		};
		styles['#hpp_avast input']['border-bottom'] = styles['#hpp_avast input']['border-top'];
		styles['#hpp_avast label'] = {
			...styles['#hpp_avast label'],
			top: '1.6rem',
			left: '1.6rem !important', /* Meh.. ¯\_(ツ)_/¯ */
			'line-height': '2rem'
		};
		styles['#hpp_avast #ddContainer input:not(:focus) + label'] = {
			opacity: 0
		};
		styles['#hpp_avast #ddContainer input.visited + label'] = {
			opacity: 1
		};
	}
	// Styling for RTL locales
	if (isRtl) {
		styles['#hpp_avast #ccContainer'] = {
			...styles['#hpp_avast #ccContainer'],
			'flex-direction': 'row-reverse',
			'padding-left': '0',
			'padding-right': '1.6rem'
		};
		styles['.ccExpCVCWrapper'] = {
			...styles['.ccExpCVCWrapper'],
			'flex-direction': 'row-reverse'
		};
		styles['#hpp_avast .card_logos'] = {
			left: '0.4rem',
			right: 'auto'
		};
		styles['#hpp_avast .available_card'] = {
			'margin-left': '1.6rem',
			'margin-right': '0'
		};
		styles['#hpp_avast input'] = {
			...styles['#hpp_avast input'],
			'text-align': 'right'
		};
		styles['#hpp_avast label'] = {
			...styles['#hpp_avast label'],
			left: 'auto !important', /* Meh.. ¯\_(ツ)_/¯ */
			right: '0'
		};
	}
	// Return HPP style object
	return styles;
};
